"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useInView } from "react-intersection-observer"
import type { components } from "umbraco"
import { resolveLinkHref } from "umbraco"
import { getNonUmbracoUrl, replaceRichTextLinks } from "utils"
import { getFaqAnswerMarkup, getFaqAnswerText } from "../../common/faqAnswer"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { useTransitionUpStyles as transitionUpStyles } from "../../transitions/useTransitionUpStyles"
import { useInViewConfig, delayTime } from "../../transitions/utils"
import Container from "../../Container"
import GradientChevron from "../../icons/GradientChevron"
import Plus from "../../icons/Plus"
import Minus from "../../icons/Minus"
import ChevronLeft from "../../icons/ChevronLeft"
import ChevronRight from "../../icons/ChevronRight"
import {
  Root,
  TitleRow,
  Title,
  ChevronWrapper,
  FAQList,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  QuestionText,
  AnswerText,
  CTAButton,
  CTAAnchor,
  CTAText,
  IconWrapper,
  AnswerRichText,
  PaginationWrapper,
  PaginationArrow,
  PaginationDot,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type FAQsData = components["schemas"]["FaqsPropertiesModel"]
type FaqItemData = components["schemas"]["FaqItemPropertiesModel"]

interface Props {
  data: FAQsData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Plus/Minus SVG icons (structural, not CMS-managed)
// ---------------------------------------------------------------------------

const ITEMS_PER_PAGE = 6

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function FAQs({ data }: Props) {
  const [expanded, setExpanded] = useState<string | false>(false)
  const [currentPage, setCurrentPage] = useState(1)
  const rootRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)

  // EGDE-510: one-shot entrance per the prototype — the title rises in first,
  // the question rows cascade in from below after it, then the footer row
  // (pagination + CTA) follows.
  const { ref: entranceRef, inView } = useInView(useInViewConfig)
  const setRootRefs = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node
      entranceRef(node)
    },
    [entranceRef],
  )
  const titleEntrance = transitionUpStyles(inView)

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  const allItems = data.items?.items ?? []

  // EGDE-362: cap the homepage FAQ block at 6 questions per page and paginate.
  const totalPages = Math.ceil(allItems.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const items = allItems.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  // EGDE-362: after a page change brings shorter content, scroll the section
  // top back into view (scroll-margin-top clears the fixed header). Runs in an
  // effect so it targets the committed layout, skipping the initial mount.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [currentPage])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
    setExpanded(false)
  }, [])

  // RTL: right arrow goes to the previous page, left arrow to the next.
  const handleRightArrow = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1))
    setExpanded(false)
  }, [])

  const handleLeftArrow = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
    setExpanded(false)
  }, [totalPages])

  const rightDisabled = currentPage <= 1
  const leftDisabled = currentPage >= totalPages

  const ctaResolved = resolveLinkHref(data.ctaLink, {
    getNonUmbracoUrl,
    getUmbracoMediaUrl,
  })
  const ctaUrl = ctaResolved?.href ?? null
  const ctaLinkType = ctaResolved?.isInternal ? "Content" : undefined

  return (
    <Container paddingMobile>
      <Root ref={setRootRefs}>
        <TitleRow style={titleEntrance}>
          <ChevronWrapper>
            <GradientChevron />
          </ChevronWrapper>
          <Title variant="h2">{data.title}</Title>
        </TitleRow>

        {/* FAQ accordion list */}
        <FAQList>
          {items.map((block, index) => {
            const props = block.content.properties as
              | FaqItemData
              | undefined
            const panelId = `faq-panel-${block.content.id ?? index}`
            const isExpanded = expanded === panelId

            return (
              <StyledAccordion
                key={block.content.id ?? index}
                expanded={isExpanded}
                onChange={handleChange(panelId)}
                disableGutters
                square
                style={transitionUpStyles(inView, delayTime + index * 0.08)}
              >
                <StyledAccordionSummary
                  expandIcon={
                    <IconWrapper>
                      {isExpanded ? <Minus /> : <Plus />}
                    </IconWrapper>
                  }
                >
                  {/*
                   * RTL: AccordionSummary is flex-direction:row.
                   * Source: [content, expandIcon]
                   * RTL renders: [expandIcon LEFT, content RIGHT]
                   * This matches the design: icon on left, question on right.
                   * EGDE-506 r2: no hover chevron next to the question — the
                   * design shows the plain text row.
                   */}
                  <QuestionText variant="body1" $expanded={isExpanded}>
                    {props?.question}
                  </QuestionText>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  {(() => {
                    const markup = getFaqAnswerMarkup(props?.answer)
                    return markup ? (
                      <AnswerRichText
                        dangerouslySetInnerHTML={{
                          __html: replaceRichTextLinks(markup),
                        }}
                      />
                    ) : (
                      <AnswerText variant="body2">
                        {getFaqAnswerText(props?.answer)}
                      </AnswerText>
                    )
                  })()}
                </StyledAccordionDetails>
              </StyledAccordion>
            )
          })}
        </FAQList>

        {/* Pagination — only when more than one page of questions (EGDE-362) */}
        {totalPages > 1 && (
          <PaginationWrapper
            style={transitionUpStyles(
              inView,
              delayTime + items.length * 0.08,
            )}
          >
            <PaginationArrow
              onClick={handleRightArrow}
              disabled={rightDisabled}
              aria-label="Previous page"
            >
              <ChevronRight />
            </PaginationArrow>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationDot
                key={page}
                $active={page === currentPage}
                onClick={() => handlePageChange(page)}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </PaginationDot>
            ))}
            <PaginationArrow
              onClick={handleLeftArrow}
              disabled={leftDisabled}
              aria-label="Next page"
            >
              <ChevronLeft />
            </PaginationArrow>
          </PaginationWrapper>
        )}

        {/* CTA button — use Link for Content, <a> for Media/External */}
        {data.ctaLabel && ctaUrl && (
          ctaLinkType === "Content" ? (
            <CTAButton
              href={ctaUrl}
              style={transitionUpStyles(
                inView,
                delayTime + items.length * 0.08,
              )}
            >
              <CTAText variant="body1">{data.ctaLabel}</CTAText>
            </CTAButton>
          ) : (
            <CTAAnchor
              href={ctaUrl}
              style={transitionUpStyles(
                inView,
                delayTime + items.length * 0.08,
              )}
            >
              <CTAText variant="body1">{data.ctaLabel}</CTAText>
            </CTAAnchor>
          )
        )}
      </Root>
    </Container>
  )
}
